import { Container } from "@/types/v2";
import {
  checkContainerConfiguration,
  normalizeLabels,
  normalizeTechs,
} from "@/lib/checkContainerConfiguration";
import { isBulkSelectable } from "@/lib/containerSelection";
import {
  isLinkSyncOn,
  LinkAdditions,
  mergeLinksIntoContainer,
  NO_SYNC_OVERRIDES,
  SyncOverrides,
} from "@/lib/mergeContainerLinks";

export type LinkKind = "technician" | "label";

export interface CatalogOption {
  value: string;
  label: string;
}

export type SyncState = "on" | "off" | "mixed";

export interface LinkRow {
  kind: LinkKind;
  /** Normalized technician id, or label name. */
  key: string;
  name: string;
  /** Which selected containers hold this link now. */
  holderIds: string[];
  holderCount: number;
  totalCount: number;
  /** Containers that will gain the link on Apply. 0 unless pending. */
  newCount: number;
  pending: boolean;
  /** True when this existing link is marked for removal from every container that holds it (WEBV3-346). */
  pendingRemove: boolean;
  coverage: string;
  /** Existing links (on holder containers) whose two-way sync is on right now. */
  syncOnCount: number;
  /** Links in the row after Apply: holders + containers that will gain it. New links start off. */
  syncTotal: number;
  /** Effective state, honoring a choice made in the dialog. */
  syncState: SyncState;
  /** True when the user has chosen a value for this row (drives CHANGED + Revert). */
  syncChanged: boolean;
}

export interface PlannedChange {
  kind: LinkKind;
  key: string;
  headline: string;
  detail: string;
  containerIds: string[];
  /** Set only on "Turn two-way sync ..." entries (WEBV3-341). */
  isSync?: true;
  /** Set only on "Unlink ..." entries (WEBV3-346). */
  isRemoval?: true;
  /** Removal entries only: how many of the affected containers have two-way sync ON for the link right now. */
  syncOnCount?: number;
}

export interface LinkConflict {
  containerId: string;
  containerName: string;
  message: string;
}

export interface LinkPlan {
  changes: PlannedChange[];
  affectedContainerIds: string[];
  conflicts: LinkConflict[];
}

const linkKeysOf = (kind: LinkKind, container: Container): string[] =>
  kind === "technician"
    ? normalizeTechs(container.linkedTechnicianId)
    : normalizeLabels(container.linkedSourceLabel);

const containerNoun = (count: number) =>
  count === 1 ? "container" : "containers";

/**
 * The containers a bulk edit will actually touch. Dispatch can never be linked
 * and is dropped (its count feeds the skip notice); ids no longer in the store
 * are dropped silently.
 */
export function resolveTargets(
  containerIds: string[],
  containers: Container[]
): { targets: Container[]; skippedDispatchCount: number } {
  const wanted = new Set(containerIds);
  const skippedDispatchCount = Array.from(wanted).filter(
    (id) => !isBulkSelectable(id)
  ).length;
  const targets = containers.filter(
    (c) => wanted.has(c.id) && isBulkSelectable(c.id)
  );
  return { targets, skippedDispatchCount };
}

function coverageText(
  holderCount: number,
  totalCount: number,
  newCount: number
): string {
  const noun = containerNoun(totalCount);
  if (newCount > 0 && holderCount > 0) {
    return `${holderCount} → ${totalCount} of ${totalCount} ${noun} · ${newCount} new`;
  }
  if (newCount > 0) {
    return `New on ${totalCount} of ${totalCount} ${noun}`;
  }
  return `${holderCount} of ${totalCount} ${noun}`;
}

const syncField = (kind: LinkKind) =>
  kind === "technician" ? "technicians" : "labels";

/** The row's state before any choice: what "Revert" returns to. */
export function baseSyncState(
  row: Pick<LinkRow, "syncOnCount" | "syncTotal">
): SyncState {
  if (row.syncOnCount === 0) return "off";
  return row.syncOnCount === row.syncTotal ? "on" : "mixed";
}

/**
 * Records the user's on/off choice for a row. Choosing the value a uniform row
 * already has clears the choice instead (no phantom CHANGED, no empty Review
 * entry); on a Mixed row either value is a real change and is always kept.
 */
export function chooseSync(
  sync: SyncOverrides,
  row: Pick<LinkRow, "kind" | "key" | "syncOnCount" | "syncTotal">,
  value: boolean
): SyncOverrides {
  const field = syncField(row.kind);
  const base = baseSyncState(row);
  const next = { ...sync[field] };
  if ((value && base === "on") || (!value && base === "off")) {
    delete next[row.key];
  } else {
    next[row.key] = value;
  }
  return { ...sync, [field]: next };
}

/** Drops a row's choice (Revert, or its pending link being removed). */
export function clearSyncChoice(
  sync: SyncOverrides,
  kind: LinkKind,
  key: string
): SyncOverrides {
  const field = syncField(kind);
  const next = { ...sync[field] };
  delete next[key];
  return { ...sync, [field]: next };
}

/**
 * One row per technician (or label) across the selection: the union of
 * everything already linked plus anything pending. A pending key that every
 * target already holds (another session linked it meanwhile) is not pending.
 * `removeKeys` marks existing links for removal from every holder (WEBV3-346);
 * removal wins over a pending add, and a key nobody holds is not a removal.
 */
export function buildLinkRows(
  kind: LinkKind,
  targets: Container[],
  pendingKeys: string[],
  resolveName: (key: string) => string,
  sync: SyncOverrides = NO_SYNC_OVERRIDES,
  removeKeys: string[] = []
): LinkRow[] {
  const keys: string[] = [];
  for (const target of targets) {
    for (const key of linkKeysOf(kind, target)) {
      if (!keys.includes(key)) keys.push(key);
    }
  }
  for (const key of pendingKeys) {
    if (!keys.includes(key)) keys.push(key);
  }

  const totalCount = targets.length;
  return keys.map((key) => {
    const holders = targets.filter((t) => linkKeysOf(kind, t).includes(key));
    const holderIds = holders.map((t) => t.id);
    const holderCount = holderIds.length;
    const removing = holderCount > 0 && removeKeys.includes(key);
    const newCount =
      !removing && pendingKeys.includes(key) ? totalCount - holderCount : 0;
    const syncOnCount = holders.filter((t) =>
      isLinkSyncOn(kind, t, key)
    ).length;
    const syncTotal = holderCount + newCount;
    const choice = sync[syncField(kind)][key];
    const syncState: SyncState =
      choice === undefined
        ? baseSyncState({ syncOnCount, syncTotal })
        : choice
          ? "on"
          : "off";
    return {
      kind,
      key,
      name: resolveName(key),
      holderIds,
      holderCount,
      totalCount,
      newCount,
      pending: newCount > 0,
      pendingRemove: removing,
      coverage: removing
        ? `Removing from ${holderCount} of ${totalCount} ${containerNoun(totalCount)}`
        : coverageText(holderCount, totalCount, newCount),
      syncOnCount,
      syncTotal,
      syncState,
      syncChanged: choice !== undefined,
    };
  });
}

/**
 * Picker options: everything in the catalog not already on 100% of the targets
 * (pending items count as covering all of them). Partly linked options say so.
 * Keys pending removal are not offered (WEBV3-346).
 */
export function getAddOptions(
  kind: LinkKind,
  targets: Container[],
  catalog: CatalogOption[],
  pendingKeys: string[],
  removeKeys: string[] = []
): CatalogOption[] {
  const totalCount = targets.length;
  if (totalCount === 0) return [];
  return catalog.flatMap((option) => {
    if (pendingKeys.includes(option.value)) return [];
    if (removeKeys.includes(option.value)) return [];
    const holders = targets.filter((t) =>
      linkKeysOf(kind, t).includes(option.value)
    ).length;
    if (holders === totalCount) return [];
    return [
      holders > 0
        ? {
            ...option,
            label: `${option.label} (on ${holders} of ${totalCount})`,
          }
        : option,
    ];
  });
}

/**
 * The Review list plus non-blocking duplicate-link conflict warnings. Conflicts
 * come from applying the additions (via the same merge the repository uses) to a
 * copy of ALL the shop's containers, then running checkContainerConfiguration on
 * each container this change gives a NEW LINK to. Containers that are already in
 * conflict but untouched, and containers touched only by a two-way sync choice
 * (conflicts never depend on sync), are not reported. Sync choices add "Turn
 * two-way sync ..." entries (isSync) after the link entries, for the containers
 * that already hold the link and whose stored value would flip. Removals
 * (WEBV3-346) come first as `isRemoval` entries and are projected into the
 * conflict check like adds.
 */
export function planLinkChanges(
  targets: Container[],
  allContainers: Container[],
  pending: LinkAdditions,
  resolveTechnicianName: (id: string) => string,
  sync: SyncOverrides = NO_SYNC_OVERRIDES
): LinkPlan {
  const changes: PlannedChange[] = [];

  const removedTechIds = new Set(
    (pending.removeTechnicianIds ?? []).map(String)
  );
  const removedLabels = new Set(pending.removeLabels ?? []);

  // Removals first (WEBV3-346): they are the destructive entries. Only
  // containers that hold the link are listed; a key nobody holds is skipped.
  const addRemovals = (
    kind: LinkKind,
    keys: Set<string>,
    nameOf: (key: string) => string
  ) => {
    for (const key of keys) {
      const holders = targets.filter((t) => linkKeysOf(kind, t).includes(key));
      if (holders.length === 0) continue;
      changes.push({
        kind,
        key,
        isRemoval: true,
        headline: `Unlink ${kind} ${nameOf(key)} from ${holders.length} of ${targets.length} ${containerNoun(targets.length)}`,
        detail: holders.map((t) => t.displayName ?? t.id).join(", "),
        containerIds: holders.map((t) => t.id),
        syncOnCount: holders.filter((t) => isLinkSyncOn(kind, t, key)).length,
      });
    }
  };
  addRemovals("technician", removedTechIds, resolveTechnicianName);
  addRemovals("label", removedLabels, (key) => key);

  for (const id of pending.technicianIds) {
    if (removedTechIds.has(id)) continue;
    const containerIds = targets
      .filter((t) => !normalizeTechs(t.linkedTechnicianId).includes(id))
      .map((t) => t.id);
    if (containerIds.length === 0) continue;
    changes.push({
      kind: "technician",
      key: id,
      headline: `Link technician ${resolveTechnicianName(id)} → ${containerIds.length} ${containerNoun(containerIds.length)}`,
      detail: `Two-way sync ${sync.technicians[id] ? "on" : "off"} for the new links`,
      containerIds,
    });
  }
  for (const label of pending.labels) {
    if (removedLabels.has(label)) continue;
    const containerIds = targets
      .filter((t) => !normalizeLabels(t.linkedSourceLabel).includes(label))
      .map((t) => t.id);
    if (containerIds.length === 0) continue;
    changes.push({
      kind: "label",
      key: label,
      headline: `Link label ${label} → ${containerIds.length} ${containerNoun(containerIds.length)}`,
      detail: `Two-way sync ${sync.labels[label] ? "on" : "off"} for the new links`,
      containerIds,
    });
  }

  // Sync-only changes: containers that already hold the link and whose stored
  // value differs from the chosen one. (Containers gaining the link are covered
  // by the link entry above and follow the same choice.)
  const addSyncChanges = (
    kind: LinkKind,
    choices: Record<string, boolean>,
    nameOf: (key: string) => string,
    removed: Set<string>
  ) => {
    for (const [key, on] of Object.entries(choices)) {
      if (removed.has(key)) continue;
      const flips = targets.filter(
        (t) =>
          linkKeysOf(kind, t).includes(key) && isLinkSyncOn(kind, t, key) !== on
      );
      if (flips.length === 0) continue;
      changes.push({
        kind,
        key,
        isSync: true,
        headline: `Turn two-way sync ${on ? "on" : "off"} for ${nameOf(key)} on ${flips.length} ${containerNoun(flips.length)}`,
        detail: flips.map((t) => t.displayName ?? t.id).join(", "),
        containerIds: flips.map((t) => t.id),
      });
    }
  };
  addSyncChanges(
    "technician",
    sync.technicians,
    resolveTechnicianName,
    removedTechIds
  );
  addSyncChanges("label", sync.labels, (key) => key, removedLabels);

  const affected = new Set(changes.flatMap((change) => change.containerIds));
  const affectedContainerIds = targets
    .map((t) => t.id)
    .filter((id) => affected.has(id));

  // Conflicts depend on links only, never on sync, so only containers that
  // gain a link are projected and checked.
  const linkAffected = new Set(
    changes
      .filter((change) => !change.isSync)
      .flatMap((change) => change.containerIds)
  );
  const projected = allContainers.map((container) =>
    linkAffected.has(container.id)
      ? mergeLinksIntoContainer(container, pending, sync).container
      : container
  );
  const conflicts: LinkConflict[] = [];
  for (const container of projected) {
    if (!linkAffected.has(container.id)) continue;
    const result = checkContainerConfiguration(container, projected);
    if (!result.valid) {
      conflicts.push({
        containerId: container.id,
        containerName: container.displayName ?? container.id,
        message: result.message,
      });
    }
  }

  return { changes, affectedContainerIds, conflicts };
}
